import type {
  AnexoNoRegistro,
  PacoteDosAutos,
  PortaDosAutos,
  RegistroDeAnexos,
} from '../types/baixarProcesso.types';

/**
 * Por que o anexo não entrou no PDF, em palavras. Os códigos vêm do servidor: os do job (o arquivo
 * não pôde ser lido) e os do core (decidiu o aviso ao montar o pacote).
 */
const MOTIVO_EM_TEXTO: Record<string, string> = {
  PDF_CRIPTOGRAFADO: 'Protegido por senha',
  PDF_CORROMPIDO: 'Arquivo corrompido ou incompleto',
  IMAGEM_ILEGIVEL: 'A imagem não pôde ser lida',
  FORMATO_NAO_RECONHECIDO: 'O conteúdo não é PDF nem imagem',
  ANEXO_GRANDE_DEMAIS: 'Passa do limite de páginas',
  ARQUIVO_AUSENTE: 'Arquivo não encontrado no armazenamento',
  ARQUIVO_DIVERGENTE: 'Arquivo diferente do registrado no processo',
  SIGILOSO: 'Marcado como sigiloso',
  EM_CONVERSAO: 'Ainda em conversão para PDF',
  ARQUIVO_INVALIDO: 'Registro do arquivo não confere',
  FORMATO_NAO_CONVERTIDO: 'Formato não convertido para PDF',
};

/** @example motivoEmTexto('PDF_CRIPTOGRAFADO') // 'Protegido por senha' */
export function motivoEmTexto(motivo: string | null): string {
  if (!motivo) return 'Não reproduzido no PDF';
  return MOTIVO_EM_TEXTO[motivo] ?? 'Não reproduzido no PDF';
}

/** @example eventoDoAnexo('03') // 'Evento 03' */
export function eventoDoAnexo(eventoNumero: string | null): string {
  return eventoNumero === null ? 'Mensagens do chat' : `Evento ${eventoNumero}`;
}

/** O destino do anexo no documento, como o registro da Secretaria mostra. */
export function resultadoDoAnexo(anexo: AnexoNoRegistro): string {
  if (anexo.tratamento !== 'INCORPORAR') return `Não entrou: ${motivoEmTexto(anexo.motivo)}`;
  return anexo.convertido ? 'Convertido de imagem' : 'Incorporado';
}

/** As páginas do anexo no PDF (para o que não entrou, a página do aviso no lugar dele). */
export function paginasDoAnexo(anexo: AnexoNoRegistro): string {
  const ultima = anexo.paginaInicial + anexo.totalPaginas - 1;
  return anexo.totalPaginas === 1
    ? `pág. ${anexo.paginaInicial}`
    : `págs. ${anexo.paginaInicial}–${ultima}`;
}

export function anexosForaDoPdf(registro: RegistroDeAnexos): AnexoNoRegistro[] {
  return registro.anexos.filter((anexo) => anexo.tratamento !== 'INCORPORAR');
}

/**
 * A tela busca o registro do pacote pronto quando há anexo fora do PDF (para mostrar e pedir a
 * ciência) e, na Secretaria, sempre que o pacote tem registro (para o registro completo). A
 * ciência pendente basta, mesmo sem a contagem (pacote lido antes dela, e o servidor respondeu
 * 409 no download): é a lista que diz do que se dá ciência.
 *
 * @example precisaDoRegistro(pacote, 'escritorio')
 */
export function precisaDoRegistro(pacote: PacoteDosAutos, porta: PortaDosAutos): boolean {
  if (pacote.status !== 'PRONTO') return false;
  if (pacote.cienciaPendente) return true;
  if (pacote.anexosNaoIncorporados === null) return false;
  return pacote.anexosNaoIncorporados > 0 || porta === 'secretaria';
}

/**
 * Quantos anexos não entraram: pelo registro quando ele já chegou, pela contagem do pacote antes
 * disso; `null` quando nenhum dos dois sabe (ciência pendente, registro ainda a caminho).
 *
 * @example totalForaDoPdf(pacote, null) // pacote.anexosNaoIncorporados
 */
export function totalForaDoPdf(
  pacote: PacoteDosAutos,
  registro: RegistroDeAnexos | null,
): number | null {
  return registro?.naoIncorporados ?? pacote.anexosNaoIncorporados;
}
